import { createSlice } from '@reduxjs/toolkit';

import { getOnboardingData } from '../utils/onboardingUtils';

import type { OnboardingState, OnboardingStep } from '../types/onboarding';
import type { PayloadAction } from '@reduxjs/toolkit';

const defaultSteps: OnboardingStep[] = [
	{
		id: 1,
		name: 'Upload Statement',
		path: '/onboarding/upload',
		description: 'Upload your first financial statement for analysis',
		completed: false,
	},
	{
		id: 2,
		name: 'Purchase Credits',
		path: '/onboarding/subscribe',
		description: 'Purchase credits to continue',
		completed: false,
	},
	{
		id: 3,
		name: 'Configure Rates',
		path: '/onboarding/config/new',
		description: 'Set up your interchange rate configuration',
		completed: false,
	},
	{
		id: 4,
		name: 'Add Team',
		path: '/onboarding/team',
		description: 'Invite team members to collaborate',
		completed: false,
	},
];

const initialState: OnboardingState = {
	currentStepId: 1,
	steps: defaultSteps,
	isComplete: false,
	uploadedFileId: undefined,
	startedAt: undefined,
	error: undefined,
};

// Load onboarding state from localStorage
const loadOnboardingFromStorage = (userEmail?: string): OnboardingState => {
	if (!userEmail) return initialState;

	try {
		const stored = getOnboardingData(userEmail);
		if (stored && (stored.currentStep || stored.currentStepId)) {
			// Handle both old (currentStep) and new (currentStepId) formats
			const currentStep = stored.currentStep || stored.currentStepId || 1;

			return {
				...initialState,
				currentStepId: currentStep,
				uploadedFileId: stored.uploadedFileId,
				steps: defaultSteps.map((step) => ({
					...step,
					completed: stored.stepsCompleted?.includes(step.id) || false,
				})),
				isComplete: stored.completed || false,
				startedAt: stored.startedAt || Date.now(),
			};
		}
	} catch (error) {
		console.error('Failed to load onboarding state from storage:', error);
	}

	return initialState;
};

const onboardingSlice = createSlice({
	name: 'onboarding',
	initialState,
	reducers: {
		startOnboarding: (
			state,
			action: PayloadAction<{ userEmail?: string; fileId?: string }>,
		) => {
			const { userEmail, fileId } = action.payload;

			// Load existing state if available
			if (userEmail) {
				const stored = loadOnboardingFromStorage(userEmail);
				Object.assign(state, stored);
			}

			// Start fresh if no stored state
			if (!state.startedAt) {
				state.currentStepId = 1;
				state.steps = defaultSteps.map((step) => ({
					...step,
					completed: false,
				}));
				state.isComplete = false;
				state.startedAt = Date.now();

				// Only mark first step as completed if starting fresh with a file
				if (fileId && state.steps[0]) {
					state.steps[0].completed = true;
				}
			}

			// Update fileId if provided (but don't auto-complete step if restoring)
			if (fileId) {
				state.uploadedFileId = fileId;
			}
		},

		completeStep: (state, action: PayloadAction<number>) => {
			const stepId = action.payload;
			const step = state.steps.find((s) => s.id === stepId);
			if (step) {
				step.completed = true;
			}
		},

		goToNextStep: (state) => {
			const currentStep = state.steps.find((s) => s.id === state.currentStepId);
			if (currentStep && state.currentStepId < state.steps.length) {
				state.currentStepId = state.currentStepId + 1;
			}
		},

		goToPreviousStep: (state) => {
			if (state.currentStepId > 1) {
				state.currentStepId = state.currentStepId - 1;
			}
		},

		goToStep: (state, action: PayloadAction<number>) => {
			const stepId = action.payload;
			if (stepId >= 1 && stepId <= state.steps.length) {
				state.currentStepId = stepId;
			}
		},

		skipStep: (state, action: PayloadAction<number>) => {
			const stepId = action.payload;
			if (stepId) {
				const step = state.steps.find((s) => s.id === stepId);
				if (step) {
					step.completed = true;
				}
			}
		},

		updateUploadedFileId: (state, action: PayloadAction<string>) => {
			state.uploadedFileId = action.payload;
			// Mark first step as completed when file is uploaded
			if (state.steps[0] && !state.steps[0].completed) {
				state.steps[0].completed = true;
			}
		},

		completeOnboarding: (state) => {
			state.isComplete = true;
			state.currentStepId = state.steps.length;
			state.steps.forEach((step) => {
				step.completed = true;
			});
		},

		resetOnboarding: (state) => {
			Object.assign(state, initialState);
		},

		setError: (state, action: PayloadAction<string>) => {
			state.error = action.payload;
		},

		clearError: (state) => {
			state.error = undefined;
		},
	},
});

export const {
	startOnboarding,
	completeStep,
	goToNextStep,
	goToPreviousStep,
	goToStep,
	skipStep,
	updateUploadedFileId,
	completeOnboarding,
	resetOnboarding,
	setError,
	clearError,
} = onboardingSlice.actions;

export default onboardingSlice.reducer;
